import React, { useEffect, useState } from 'react'
import { Modal, Form, Button, Card, Alert, Spinner, Row, Col } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { Channel, ChannelProvider, ChannelType } from '../../../types/assignmentGroups'
import { MultiSelectWithActions } from '../../../components/MultiSelectWithActions'
import { TEST_TWILIO_CREDENTIALS } from '../../../gql/mutations/channelMutations'

interface ChannelFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: Partial<Channel>) => Promise<Channel>
  saving: boolean
  initialData?: Channel | null
  groupMembers: Array<{ value: string; label: string }>
}

export const ChannelFormModal: React.FC<ChannelFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  saving,
  initialData,
  groupMembers,
}) => {
  const [testTwilioCredentialsMutation, { loading: testingCredentialsLoading }] = useMutation(TEST_TWILIO_CREDENTIALS)

  const [provider] = useState<ChannelProvider>('TWILIO')
  const [channelType, setChannelType] = useState<ChannelType>('SMS')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [assignedMemberIds, setAssignedMemberIds] = useState<string[]>([])
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [savedChannelId, setSavedChannelId] = useState<number | null>(null)
  const [testMessage, setTestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Initialize from initialData or reset
  useEffect(() => {
    if (initialData) {
      setPhoneNumber(initialData.phone_number)
      setIsActive(initialData.is_active)
      setChannelType(initialData.channel_type || 'SMS')
      setAssignedMemberIds((initialData.assigned_member_ids || []).filter((id) => id != null).map((id) => id.toString()))

      let parsedCreds = initialData.credentials || {}
      if (typeof parsedCreds === 'string') {
        try {
          parsedCreds = JSON.parse(parsedCreds)
        } catch (e) {
          parsedCreds = {}
        }
      }
      // Ensure webhook_token is in credentials
      if (initialData.webhook_token && typeof parsedCreds === 'object') {
        parsedCreds = { ...parsedCreds, webhook_token: initialData.webhook_token }
      }
      setCredentials(parsedCreds)
      setSavedChannelId(initialData.id)
    } else {
      setPhoneNumber('')
      setIsActive(true)
      setChannelType('SMS')
      setAssignedMemberIds([])
      setCredentials({})
      setSavedChannelId(null)
    }
    setError(null)
  }, [initialData, isOpen])

  const handleSave = async () => {
    setError(null)

    // Validate Twilio credentials
    const requiredFields = ['account_sid', 'auth_token']
    const missingFields = requiredFields.filter((field: string) => !credentials[field])
    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`)
      return
    }

    try {
      const data: Partial<Channel> = {
        id: savedChannelId || undefined,
        provider,
        channel_type: channelType,
        phone_number: phoneNumber,
        is_active: isActive,
        assigned_member_ids: assignedMemberIds.map((id) => parseInt(id, 10)),
        credentials,
      }

      const savedChannel = await onSave(data)
      if (savedChannel && savedChannel.id) {
        setSavedChannelId(savedChannel.id)
        let parsedCreds = savedChannel.credentials || {}
        if (typeof parsedCreds === 'string') {
          try {
            parsedCreds = JSON.parse(parsedCreds)
          } catch (e) {
            parsedCreds = {}
          }
        }
        // Ensure webhook_token is in credentials
        if (savedChannel.webhook_token && typeof parsedCreds === 'object') {
          parsedCreds = { ...parsedCreds, webhook_token: savedChannel.webhook_token }
        }
        setCredentials(parsedCreds)
      }
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.error || 'Error saving channel'
      setError(errorMessage)
      console.error('Save channel error:', err)
    }
  }

  const handleCredentialChange = (field: string, value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleTestCredentials = async () => {
    setTestMessage(null)
    const missingFields = ['account_sid', 'auth_token'].filter((field) => !credentials[field])

    if (missingFields.length > 0) {
      setTestMessage({
        type: 'error',
        text: `Missing fields: ${missingFields.join(', ')}`,
      })
      return
    }

    try {
      const result = await testTwilioCredentialsMutation({
        variables: {
          accountSid: credentials.account_sid,
          authToken: credentials.auth_token,
        },
      })

      if (result.data?.testTwilioCredentials?.success) {
        setTestMessage({
          type: 'success',
          text: `Credentials verified successfully! Account: ${result.data.testTwilioCredentials.accountName}`,
        })
      } else {
        setTestMessage({
          type: 'error',
          text: result.data?.testTwilioCredentials?.error || 'Failed to verify credentials. Please check and try again.',
        })
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to verify credentials. Please check and try again.'
      setTestMessage({
        type: 'error',
        text: errorMessage,
      })
      console.error(err)
    }
  }

  const getWebhookUrl = () => {
    if (!savedChannelId) return null
    const token = credentials.webhook_token || '[token]'
    if (provider === 'TWILIO') {
      return `https://app.policytools.com/webhooks/twilio/${channelType.toLowerCase()}/${token}/`
    }
    if (provider === 'BOTMAKER') {
      return `https://app.policytools.com/webhooks/botmaker/${token}/`
    }
    return null
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`)
    }).catch(() => {
      toast.error('Failed to copy to clipboard')
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>
          {savedChannelId ? 'Edit Twilio Channel' : 'Add Twilio Channel'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant='danger' className='mb-3'>{error}</Alert>}

        <Form>
          <Row className='g-3 mb-3'>
            <Col md={6}>
              <Form.Group>
                <Form.Label className='fw-semibold'>Channel Type</Form.Label>
                <Form.Select value={channelType} onChange={(e) => setChannelType(e.target.value as ChannelType)} size='sm'>
                  <option value='SMS'>SMS</option>
                  <option value='WHATSAPP'>WhatsApp</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className='fw-semibold'>Twilio Phone Number</Form.Label>
                <Form.Control
                  type='text'
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder='+1234567890'
                  size='sm'
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className='mb-3'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <Form.Label className='fw-semibold mb-0'>Credentials</Form.Label>
              <Form.Switch
                id='isActive'
                label='Active'
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className='fw-semibold'
              />
            </div>
            <Card className='bg-light p-3'>
              <Row className='g-3 mb-3'>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className='fw-semibold' style={{ fontSize: '0.875rem' }}>Account SID</Form.Label>
                    <Form.Control
                      type='text'
                      value={credentials.account_sid || ''}
                      onChange={(e) => handleCredentialChange('account_sid', e.target.value)}
                      placeholder='Your Twilio Account SID'
                      size='sm'
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className='fw-semibold' style={{ fontSize: '0.875rem' }}>Auth Token</Form.Label>
                    <Form.Control
                      type='password'
                      value={credentials.auth_token || ''}
                      onChange={(e) => handleCredentialChange('auth_token', e.target.value)}
                      placeholder='Your Twilio Auth Token'
                      size='sm'
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className='d-flex align-items-end'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleTestCredentials}
                    disabled={testingCredentialsLoading}
                    className='w-100'
                  >
                    {testingCredentialsLoading ? (
                      <>
                        <Spinner as='span' animation='border' size='sm' />
                      </>
                    ) : (
                      'Test'
                    )}
                  </Button>
                </Col>
              </Row>
              {testMessage && (
                <Alert variant={testMessage.type === 'success' ? 'success' : 'danger'} className='mb-0'>
                  {testMessage.text}
                </Alert>
              )}
            </Card>
          </Form.Group>

          {groupMembers.length > 0 && (
            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold'>Assigned Members</Form.Label>
              <Form.Text className='d-block text-muted mb-2'>
                Empty = all group members. Select to assign to specific members only.
              </Form.Text>
              <MultiSelectWithActions
                value={groupMembers.filter((opt) => assignedMemberIds.includes(opt.value))}
                onChange={(selected: any) => {
                  const values = Array.isArray(selected)
                    ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                    : []
                  setAssignedMemberIds(values)
                }}
                options={groupMembers}
                placeholder='Select members...'
              />
            </Form.Group>
          )}

          {savedChannelId && getWebhookUrl() && (
            <>
              <Card className='bg-info-light p-3 mb-3'>
                <Form.Group>
                  <Form.Label className='fw-semibold mb-2'>Webhook URL (Inbound Messages)</Form.Label>
                  <div className='d-flex gap-2'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={getWebhookUrl() || ''}
                      className='font-monospace'
                      style={{ fontSize: '0.875rem' }}
                      size='sm'
                    />
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() => copyToClipboard(getWebhookUrl() || '', 'Webhook URL')}
                    >
                      <i className='bi bi-files' /> Copy
                    </Button>
                  </div>
                  <Form.Text className='d-block mt-2 text-muted'>
                    Configure this URL in your {provider} webhook settings for incoming messages
                  </Form.Text>
                </Form.Group>
              </Card>

              <Card className='bg-warning-light p-3 mb-3'>
                <Form.Group>
                  <Form.Label className='fw-semibold mb-2'>Status Callback URL (Delivery Receipts)</Form.Label>
                  <div className='d-flex gap-2'>
                    <Form.Control
                      type='text'
                      readOnly
                      value='https://app.policytools.com/webhooks/twilio/status/'
                      className='font-monospace'
                      style={{ fontSize: '0.875rem' }}
                      size='sm'
                    />
                    <Button
                      variant='outline-primary'
                      size='sm'
                      onClick={() => copyToClipboard('https://app.policytools.com/webhooks/twilio/status/', 'Status Callback URL')}
                    >
                      <i className='bi bi-files' /> Copy
                    </Button>
                  </div>
                  <Form.Text className='d-block mt-2 text-muted'>
                    Configure this URL in your Twilio webhook settings for delivery status updates (✓✓)
                  </Form.Text>
                </Form.Group>
              </Card>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner as='span' animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            'Save Channel'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
